import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, ChevronDown, Instagram, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { t, tr } from "../i18n/translations";
import { SERVICE_PAGES, SERVICE_PAGE_LIST } from "../i18n/service-pages";
import { tService } from "../i18n/service-page-strings";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import BookingDialog from "../components/site/BookingDialog";

export default function ServicePage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const navigate = useNavigate();
  const richPage = SERVICE_PAGES[slug];
  const [page, setPage] = useState(richPage || null);
  const [service, setService] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let active = true;
    setResolved(false); // eslint-disable-line
    setPage(richPage || null);

    const buildLitePage = (s) => ({
      backend_slug: s.slug,
      hero_image: s.image_url,
      gallery_category: s.slug === "bridal" ? "bridal" : s.slug === "editorial" ? "editorial" : "events",
      eyebrow: { es: s.title_es, en: s.title_en, jp: s.title_jp },
      title: { es: s.title_es, en: s.title_en, jp: s.title_jp },
      intro: { es: s.description_es, en: s.description_en, jp: s.description_jp },
      seo_title: {
        es: `${s.title_es} en Aguascalientes · Abril Suarez`,
        en: `${s.title_en} · Aguascalientes · Abril Suarez`,
        jp: `${s.title_jp} アグアスカリエンテス · アブリル・スアレス`,
      },
      seo_desc: { es: s.description_es, en: s.description_en, jp: s.description_jp },
      process: { es: [], en: [], jp: [] },
      includes: { es: [], en: [], jp: [] },
      faq: { es: [], en: [], jp: [] },
      __lite: true,
    });

    (async () => {
      try {
        const { data } = await api.get("/services");
        const list = data || [];
        // Match rich page by backend_slug, else match URL slug directly to backend slug
        let svc = null;
        if (richPage) {
          svc = list.find((s) => s.slug === richPage.backend_slug);
        } else {
          svc = list.find((s) => s.slug === slug);
          if (svc && active) setPage(buildLitePage(svc));
        }
        if (!svc && !richPage) {
          // Service not found anywhere → redirect home
          navigate("/", { replace: true });
          return;
        }
        if (active) setService(svc || null);
      } catch {
        if (!richPage && active) navigate("/", { replace: true });
        return;
      } finally {
        if (active) setResolved(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [slug, richPage, navigate]);

  useEffect(() => {
    if (!page) return;
    api.get(`/gallery?category=${page.gallery_category}`).then((r) => setGallery(r.data || [])).catch(() => {});
  }, [page]);

  const otherServices = useMemo(() => SERVICE_PAGE_LIST.filter((p) => p.slug !== slug), [slug]);

  if (!page && !resolved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] overline text-[#5C5C5C]">Cargando…</div>
    );
  }
  if (!page) return null;

  const title = tr(page.title, lang);
  const intro = tr(page.intro, lang);
  const seoTitle = tr(page.seo_title, lang);
  const seoDesc = tr(page.seo_desc, lang);
  const process = page.process[lang] || page.process.es;
  const includes = page.includes[lang] || page.includes.es;
  const faq = page.faq[lang] || page.faq.es;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta name="keywords" content={`maquillista Aguascalientes, ${tr(page.eyebrow, "es")}, ${tr(page.eyebrow, "en")}, Abril Suarez`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:image" content={page.hero_image} />
        <link rel="canonical" href={`${typeof window !== "undefined" ? window.location.origin : ""}/servicios/${slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: tr(page.eyebrow, lang),
          description: seoDesc,
          provider: {
            "@type": "BeautySalon",
            name: "Abril Suarez Makeup Artist",
            address: { "@type": "PostalAddress", addressLocality: "Aguascalientes", addressCountry: "MX" },
            url: "/",
          },
          areaServed: "Aguascalientes, México",
          image: page.hero_image,
          ...(service?.price_from && {
            offers: {
              "@type": "Offer",
              priceCurrency: "MXN",
              price: service.price_from,
              availability: "https://schema.org/InStock",
            },
          }),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faq.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        })}</script>
      </Helmet>

      <Header onBook={() => setBookingOpen(true)} />

      <main className="bg-[#FAFAFA]">
        {/* HERO */}
        <section className="pt-28 pb-16 sm:pt-32 sm:pb-20" data-testid="service-hero">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
            <Link to="/" className="overline text-[#8B6D5E] link-underline" data-testid="service-back">
              {tr(tService.back, lang)}
            </Link>

            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mt-10 items-end">
              <div className="lg:col-span-7">
                <p className="overline text-[#8B6D5E] mb-4">{tr(page.eyebrow, lang)}</p>
                <h1 className="font-serif text-[12vw] sm:text-[8vw] lg:text-[5.5vw] leading-[1] tracking-tighter text-[#1A1A1A]" data-testid="service-title">
                  {title}
                </h1>
                <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-2xl">{intro}</p>
                <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center">
                  <button className="btn-primary" onClick={() => setBookingOpen(true)} data-testid="service-book-btn">
                    {tr(tService.book_cta, lang)} <ArrowRight size={14} />
                  </button>
                  {service && (
                    <p className="overline text-[#5C5C5C]">
                      {tr(tService.from, lang)} ${service.price_from.toLocaleString("es-MX")} {tr(tService.mxn, lang)}
                    </p>
                  )}
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="zoom-frame aspect-[3/4]">
                  <img src={page.hero_image} alt={tr(page.eyebrow, lang)} loading="eager" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROCESS */}
        {process.length > 0 && (
        <section className="bg-[#F4F0EC] py-24 sm:py-32" data-testid="service-process">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
            <p className="overline text-[#8B6D5E] mb-4">01</p>
            <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1] mb-16 max-w-2xl">
              {tr(tService.process_title, lang)}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
              {process.map((step, i) => (
                <div key={i} className="border-t border-[#1A1A1A] pt-6" data-testid={`process-step-${i}`}>
                  <p className="font-serif italic text-5xl text-[#8B6D5E] leading-none mb-6">0{i + 1}</p>
                  <h3 className="font-serif text-2xl tracking-tight mb-3">{step.t}</h3>
                  <p className="text-sm leading-relaxed text-[#5C5C5C]">{step.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        )}

        {/* INCLUDES */}
        {includes.length > 0 && (
        <section className="bg-[#FAFAFA] py-24 sm:py-32" data-testid="service-includes">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <p className="overline text-[#8B6D5E] mb-4">02</p>
              <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1]">
                {tr(tService.includes_title, lang)}
              </h2>
            </div>
            <ul className="lg:col-span-8 divide-y divide-[#E8E1DA] border-t border-b border-[#E8E1DA]">
              {includes.map((line, i) => (
                <li key={i} className="py-6 flex items-start gap-6" data-testid={`include-${i}`}>
                  <span className="overline text-[#8B6D5E] pt-1">0{i + 1}</span>
                  <span className="font-serif text-2xl tracking-tight">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
        )}

        {/* GALLERY */}
        {gallery.length > 0 && (
          <section className="bg-[#F4F0EC] py-24 sm:py-32" data-testid="service-gallery">
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
              <p className="overline text-[#8B6D5E] mb-4">03</p>
              <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1] mb-12">
                {tr(tService.gallery_title, lang)}
              </h2>
              <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 lg:gap-8">
                {gallery.map((it, i) => (
                  <div key={it.id} className="break-inside-avoid mb-6 lg:mb-8 zoom-frame" data-testid={`service-gallery-${i}`}>
                    <img src={it.image_url} alt={it.title || tr(page.eyebrow, lang)} loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {faq.length > 0 && (
        <section className="bg-[#FAFAFA] py-24 sm:py-32" data-testid="service-faq">
          <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
            <p className="overline text-[#8B6D5E] mb-4">04</p>
            <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1] mb-12">
              {tr(tService.faq_title, lang)}
            </h2>
            <div className="border-t border-[#E8E1DA]">
              {faq.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <div key={i} className="border-b border-[#E8E1DA]" data-testid={`faq-${i}`}>
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-8 py-6 text-left"
                      data-testid={`faq-toggle-${i}`}
                      aria-expanded={isOpen}
                    >
                      <span className="font-serif text-2xl sm:text-3xl tracking-tight pr-4">{item.q}</span>
                      <ChevronDown
                        size={22}
                        className={`shrink-0 text-[#8B6D5E] transition-transform duration-500 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <div
                      className={`grid transition-all duration-500 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"}`}
                    >
                      <div className="overflow-hidden text-[#5C5C5C] leading-relaxed text-base max-w-3xl">
                        {item.a}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        )}

        {/* CTA BLOCK */}
        <section className="bg-[#1A1A1A] text-[#FAFAFA] py-24 sm:py-32" data-testid="service-cta">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="overline text-[#8B6D5E] mb-4">05</p>
              <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1] mb-6">
                {tService.cta_block[lang]?.title || tService.cta_block.es.title}
              </h2>
              <p className="text-lg text-[#A8A8A8] max-w-xl mb-10">
                {tService.cta_block[lang]?.sub || tService.cta_block.es.sub}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                <button onClick={() => setBookingOpen(true)} className="btn-primary !bg-[#FAFAFA] !text-[#1A1A1A] !border-[#FAFAFA] hover:!bg-[#8B6D5E] hover:!text-[#FAFAFA] hover:!border-[#8B6D5E]" data-testid="service-cta-book">
                  {tr(tService.book_cta, lang)}
                </button>
                <a
                  href="https://wa.me/message/SG2ELRTG32LVF1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="overline flex items-center gap-2 link-underline"
                  data-testid="service-cta-whatsapp"
                >
                  <MessageCircle size={16} /> WhatsApp
                </a>
                <a
                  href="https://www.instagram.com/abrilsuamkp/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="overline flex items-center gap-2 link-underline"
                  data-testid="service-cta-instagram"
                >
                  <Instagram size={16} /> @abrilsuamkp
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="zoom-frame aspect-[4/5]">
                <img src={page.hero_image} alt={tr(page.eyebrow, lang)} loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* RELATED SERVICES */}
        <section className="bg-[#FAFAFA] py-24 sm:py-32 border-t border-[#E8E1DA]" data-testid="related-services">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
            <p className="overline text-[#8B6D5E] mb-4">06</p>
            <h2 className="font-serif text-5xl sm:text-6xl tracking-tighter leading-[1] mb-12">
              {tr(tService.related, lang)}
            </h2>
            <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
              {otherServices.map((o) => (
                <Link
                  key={o.slug}
                  to={`/servicios/${o.slug}`}
                  className="group block"
                  data-testid={`related-${o.slug}`}
                >
                  <div className="zoom-frame aspect-[4/5] mb-5">
                    <img src={o.hero_image} alt={tr(o.eyebrow, lang)} loading="lazy" />
                  </div>
                  <p className="overline text-[#8B6D5E] mb-2">{tr(o.eyebrow, lang)}</p>
                  <h3 className="font-serif text-2xl tracking-tight group-hover:text-[#8B6D5E] transition-colors">
                    {tr(o.title, lang)}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <BookingDialog
        open={bookingOpen}
        onClose={() => setBookingOpen(false)}
        defaultService={page.backend_slug}
      />
    </>
  );
}
